import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { Target, Users, Award, Heart, Dumbbell, CheckCircle2, ArrowRight, ChevronRight } from 'lucide-react';

const AboutPage = () => {
  useEffect(() => {
    document.title = 'About Us | Sport Fitt Pro';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* Banner */}
        <section className="relative h-80 overflow-hidden bg-ink-950">
          <img src={siteImages.aboutBanner} alt="About" className="absolute inset-0 w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/80 to-transparent" />
          <div className="absolute bottom-0 right-0 w-2/5 h-2 bg-athletic-500" />
          <div className="relative h-full max-w-7xl mx-auto px-4 lg:px-8 flex items-end pb-12">
            <div>
              <nav className="flex items-center gap-2 text-white/60 text-xs font-bold uppercase tracking-wider mb-4">
                <Link to="/" className="hover:text-athletic-400 transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-white">About</span>
              </nav>
              <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Our Story</p>
              <h1 className="font-display text-5xl md:text-7xl text-white leading-none tracking-wider">
                ABOUT <span className="text-athletic-500">SPORT FITT PRO</span>
              </h1>
              <p className="text-white/70 mt-4 max-w-lg text-sm">
                Premium sports gear and fitness equipment for athletes who demand excellence.
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20">

          {/* Mission Section */}
          <div className="grid lg:grid-cols-2 gap-14 items-center mb-24">
            <div>
              <p className="text-athletic-500 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Our Mission</p>
              <h2 className="font-display text-5xl lg:text-6xl text-ink-900 leading-none tracking-wider mb-6">
                EMPOWER THE <span className="text-athletic-500">ATHLETE</span> IN YOU.
              </h2>
              <p className="text-ink-600 text-lg leading-relaxed mb-5">
                At Sport Fitt Pro, we believe the right equipment transforms training. Our mission: arm athletes with premium gear that helps them crush their goals.
              </p>
              <p className="text-ink-500 leading-relaxed mb-8">
                Every product in our catalog is hand-selected and performance-tested. Whether you train at home, hit the gym or push your limits outdoors — we've got the gear to match your grind.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Quality First', 'Customer Focused', 'Performance Driven'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 px-4 py-2.5 bg-ink-50 border border-ink-100">
                    <CheckCircle2 className="w-4 h-4 text-athletic-500" />
                    <span className="text-ink-900 text-sm font-bold uppercase tracking-wider">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square overflow-hidden border-2 border-ink-100">
                <img src={siteImages.heroLifestyle} alt="Athletes training" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-athletic-500 text-white px-7 py-5 shadow-2xl">
                <p className="font-display text-5xl leading-none">8+</p>
                <p className="text-[10px] font-black uppercase tracking-[0.3em] mt-2">Premium Products</p>
              </div>
              <div className="absolute -top-4 -right-4 bg-ink-950 text-volt-400 px-5 py-3 font-black text-xs uppercase tracking-wider border-l-4 border-volt-400">
                EST. 2025
              </div>
            </div>
          </div>

          {/* Values Section */}
          <div className="mb-24">
            <div className="text-center mb-12">
              <p className="text-athletic-500 text-[11px] font-black tracking-[0.4em] uppercase mb-3">What Drives Us</p>
              <h2 className="font-display text-5xl text-ink-900 tracking-wider">OUR CORE VALUES</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: Target, title: 'Performance', desc: 'Every product tested to meet elite standards.' },
                { icon: Users, title: 'Community', desc: 'Built by athletes, for athletes. We get it.' },
                { icon: Award, title: 'Quality', desc: 'Premium materials that outlast the reps.' },
                { icon: Heart, title: 'Passion', desc: 'Fueled by love for sport and fitness.' },
              ].map((v, i) => (
                <div key={i} className="group bg-ink-50 border-b-4 border-transparent hover:border-athletic-500 hover:bg-ink-900 hover:text-white p-8 transition-all duration-300">
                  <div className="w-14 h-14 bg-athletic-500 group-hover:bg-volt-400 flex items-center justify-center mb-5 transition-colors">
                    <v.icon className="w-6 h-6 text-white group-hover:text-ink-900" strokeWidth={2.2} />
                  </div>
                  <h3 className="font-display text-2xl tracking-wider mb-3">{v.title.toUpperCase()}</h3>
                  <p className="text-sm text-ink-500 group-hover:text-white/80 leading-relaxed transition-colors">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-ink-950 text-white p-10 lg:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <img src={siteImages.ctaBackground} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative">
              <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mb-4">Ready To Perform?</p>
              <h2 className="font-display text-5xl md:text-6xl tracking-wider leading-none mb-6">
                JOIN THE <span className="text-athletic-500">SPORT FITT</span> FAMILY
              </h2>
              <p className="text-white/70 mb-8 max-w-lg mx-auto">
                Gear up with the premium equipment serious athletes trust.
              </p>
              <Link to="/shop" className="inline-flex items-center gap-3 px-8 py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-black uppercase tracking-wider transition-all">
                Shop The Collection <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
